import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, ListChecks, Plus, Trash2, X, ExternalLink, CheckCircle2, AlertCircle, FileText,
} from 'lucide-react';
import { marketingBlogAPI } from '../../api/api';
import KpiCard, { fmtNum, fmtPct, fmtFecha } from '../../components/marketing/KpiCard';

const ESTADOS = [
  { v: 'idea', label: 'Idea', color: 'gray' },
  { v: 'brief', label: 'Brief', color: 'blue' },
  { v: 'en_redaccion', label: 'En redacción', color: 'amber' },
  { v: 'listo', label: 'Listo', color: 'purple' },
  { v: 'publicado', label: 'Publicado', color: 'emerald' },
  { v: 'descartado', label: 'Descartado', color: 'red' },
];

const ESTADO_BADGE = {
  idea: 'bg-gray-100 text-gray-700 border-gray-300',
  brief: 'bg-blue-50 text-blue-700 border-blue-200',
  en_redaccion: 'bg-amber-50 text-amber-700 border-amber-200',
  listo: 'bg-purple-50 text-purple-700 border-purple-200',
  publicado: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  descartado: 'bg-red-50 text-red-700 border-red-200',
};

export default function MarketingBlogPlanEditorial() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCluster, setFilterCluster] = useState('todos');
  const [filterEstado, setFilterEstado] = useState('todos');
  const [editing, setEditing] = useState(null);

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketingBlogAPI.planEditorial();
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error cargando plan editorial');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const guardar = async (entry) => {
    try {
      if (entry.id) {
        await marketingBlogAPI.planEditorialUpdate(entry.id, entry);
      } else {
        await marketingBlogAPI.planEditorialCreate(entry);
      }
      setEditing(null);
      await cargar();
    } catch (e) {
      alert('Error guardando: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const borrar = async (id) => {
    if (!window.confirm('¿Borrar esta entrada del plan?')) return;
    try {
      await marketingBlogAPI.planEditorialDelete(id);
      await cargar();
    } catch (e) {
      alert('Error borrando: ' + e.message);
    }
  };

  const confirmarMatch = async (entry) => {
    if (!entry.match) return;
    if (!window.confirm(`¿Confirmar que este post del plan corresponde al post publicado "${entry.match.title}"?`)) return;
    await guardar({
      ...entry,
      slug_publicado: entry.match.slug,
      estado: 'publicado',
    });
  };

  const visibles = useMemo(() => {
    if (!data) return [];
    let arr = data.entries.slice();
    if (filterCluster !== 'todos') arr = arr.filter((e) => (e.cluster || '') === filterCluster);
    if (filterEstado !== 'todos') arr = arr.filter((e) => e.estado === filterEstado);
    return arr;
  }, [data, filterCluster, filterEstado]);

  if (loading) return <div className="p-6 text-center text-gray-500">Cargando plan editorial…</div>;
  if (error) return <div className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">{error}</div>;
  if (!data) return null;

  const clusters = ['todos', ...(data.clusters || []).map((c) => c.cluster)];

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Posts en el plan" value={fmtNum(data.total)} icon={ListChecks} />
        <KpiCard label="Publicados (explícitos)" value={fmtNum(data.publicados_explicitos)} icon={CheckCircle2} />
        <KpiCard label="Sugerencias auto-match" value={fmtNum(data.auto_match)} icon={AlertCircle} hint="similitud título ≥75%" />
        <KpiCard label="Cobertura" value={fmtPct(data.cobertura)} icon={FileText} hint="publicado o con match" />
      </div>

      {/* Resumen por cluster */}
      {data.clusters?.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Cobertura por cluster</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.clusters.map((c) => (
              <div key={c.cluster} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 text-sm">
                <span className="font-mono text-xs text-gray-600 truncate" title={c.cluster}>{c.cluster}</span>
                <div className="flex items-center gap-2">
                  <div className="w-20 bg-gray-200 rounded-full h-1.5">
                    <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${c.cobertura * 100}%` }} />
                  </div>
                  <span className="text-xs text-gray-700 tabular-nums w-12 text-right">
                    {c.publicado_o_match}/{c.total}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtros + acción */}
      <div className="flex flex-wrap items-center gap-3 bg-white border rounded-lg p-3">
        <select
          value={filterCluster}
          onChange={(e) => setFilterCluster(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
        >
          {clusters.map((c) => <option key={c} value={c}>{c === 'todos' ? 'Todos los clusters' : c}</option>)}
        </select>
        <select
          value={filterEstado}
          onChange={(e) => setFilterEstado(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
        >
          <option value="todos">Todos los estados</option>
          {ESTADOS.map((e) => <option key={e.v} value={e.v}>{e.label}</option>)}
        </select>
        <span className="text-xs text-gray-500">{visibles.length} de {data.total}</span>
        <div className="flex-1" />
        <button
          onClick={() => setEditing({
            titulo: '', cluster: '', keyword_objetivo: '', intencion: 'informacional',
            estado: 'idea', fecha_objetivo: '', notas: '',
          })}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-duetto-primary text-white text-sm rounded-md hover:bg-opacity-90"
        >
          <Plus className="w-4 h-4" /> Nueva entrada
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 border-b bg-gray-50">
              <tr>
                <th className="text-left py-2 px-2 font-medium w-10">#</th>
                <th className="text-left py-2 px-2 font-medium">Título</th>
                <th className="text-left py-2 px-2 font-medium">Cluster / KW</th>
                <th className="text-left py-2 px-2 font-medium">Estado</th>
                <th className="text-left py-2 px-2 font-medium">Fecha objetivo</th>
                <th className="text-left py-2 px-2 font-medium">Post publicado</th>
                <th className="py-2 px-2 font-medium text-right w-20">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((e) => (
                <tr key={e.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-2 px-2 text-gray-500 text-xs tabular-nums">{e.numero || '—'}</td>
                  <td className="py-2 px-2">
                    <div className="text-gray-900 font-medium">{e.titulo}</div>
                    {e.notas && <div className="text-[10px] text-gray-500 mt-0.5">{e.notas}</div>}
                  </td>
                  <td className="py-2 px-2">
                    <div className="text-xs text-gray-700 font-mono truncate" title={e.cluster}>{e.cluster || '—'}</div>
                    {e.keyword_objetivo && (
                      <div className="text-[10px] text-gray-500 truncate" title={e.keyword_objetivo}>
                        kw: {e.keyword_objetivo}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2">
                    <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${ESTADO_BADGE[e.estado] || ESTADO_BADGE.idea}`}>
                      {ESTADOS.find((s) => s.v === e.estado)?.label || e.estado}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-xs text-gray-700">{e.fecha_objetivo ? fmtFecha(e.fecha_objetivo) : <span className="text-gray-400">Buffer</span>}</td>
                  <td className="py-2 px-2">
                    {e.estado === 'publicado' && e.slug_publicado ? (
                      <Link
                        to={`/marketing/blog/posts/${e.slug_publicado}`}
                        className="text-emerald-700 hover:underline text-xs inline-flex items-center gap-1"
                        title="Ver detalle"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        {e.slug_publicado.slice(0, 32)}{e.slug_publicado.length > 32 ? '…' : ''}
                      </Link>
                    ) : e.match ? (
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/marketing/blog/posts/${e.match.slug}`}
                          className={`text-xs hover:underline inline-flex items-center gap-1 ${
                            e.match.score >= 85 ? 'text-emerald-700' : 'text-amber-700'
                          }`}
                          title={`Sugerencia auto-match: ${e.match.title} (${e.match.score}%)`}
                        >
                          <AlertCircle className="w-3 h-3" />
                          {e.match.slug.slice(0, 28)}… ({e.match.score}%)
                        </Link>
                        <button
                          onClick={() => confirmarMatch(e)}
                          className="text-[10px] text-emerald-700 underline hover:no-underline"
                          title="Confirmar que es este post"
                        >
                          ✓ confirmar
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right whitespace-nowrap">
                    <button onClick={() => setEditing({ ...e })} className="text-duetto-primary hover:underline text-xs mr-2">Editar</button>
                    <button onClick={() => borrar(e.id)} className="text-red-600 hover:underline text-xs inline-flex items-center gap-0.5" title="Borrar">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
              {visibles.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-sm text-gray-500">Sin entradas con esos filtros.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <PlanEntryFormModal entry={editing} onSave={guardar} onClose={() => setEditing(null)} />}
    </div>
  );
}

function PlanEntryFormModal({ entry, onSave, onClose }) {
  const [form, setForm] = useState(entry);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="font-semibold text-gray-900">{form.id ? 'Editar entrada del plan' : 'Nueva entrada'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <Field label="Título *">
            <input
              value={form.titulo || ''}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Cluster">
              <input
                value={form.cluster || ''}
                onChange={(e) => setForm({ ...form, cluster: e.target.value })}
                placeholder="/empresas/"
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </Field>
            <Field label="Número (orden)">
              <input
                type="number"
                value={form.numero || ''}
                onChange={(e) => setForm({ ...form, numero: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </Field>
          </div>
          <Field label="Keyword objetivo">
            <input
              value={form.keyword_objetivo || ''}
              onChange={(e) => setForm({ ...form, keyword_objetivo: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Intención">
              <select
                value={form.intencion || 'informacional'}
                onChange={(e) => setForm({ ...form, intencion: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm"
              >
                <option value="informacional">Informacional</option>
                <option value="transaccional">Transaccional</option>
                <option value="comparativa">Comparativa</option>
              </select>
            </Field>
            <Field label="Estado">
              <select
                value={form.estado || 'idea'}
                onChange={(e) => setForm({ ...form, estado: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm"
              >
                {ESTADOS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha objetivo">
              <input
                type="date"
                value={(form.fecha_objetivo || '').slice(0, 10)}
                onChange={(e) => setForm({ ...form, fecha_objetivo: e.target.value || null })}
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </Field>
            <Field label="Slug publicado (opcional)">
              <input
                value={form.slug_publicado || ''}
                onChange={(e) => setForm({ ...form, slug_publicado: e.target.value })}
                placeholder="catering-empresas-madrid"
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </Field>
          </div>
          <Field label="Notas">
            <textarea
              value={form.notas || ''}
              onChange={(e) => setForm({ ...form, notas: e.target.value })}
              rows={3}
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2 border-t p-3">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border hover:bg-gray-50">Cancelar</button>
          <button
            onClick={() => onSave(form)}
            disabled={!form.titulo?.trim()}
            className="px-3 py-1.5 text-sm rounded-md bg-duetto-primary text-white hover:bg-opacity-90 disabled:opacity-50"
          >Guardar</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}
